import { isNotNilOrEmpty } from 'ramda-adjunct';
import React, { useEffect, useState } from 'react';
import { adjust, assoc, assocPath, compose, defaultTo, findIndex, gt, ifElse, isNil, length, lensPath, lensProp, move, not, over, path, pick, prop, propEq, remove, isNotNil, isNotEmpty } from 'ramda';
import { useHistory } from 'react-router-dom';
import { DEFAULT_COLOR } from '../_CONST';
import { getVariablesForRules } from '../utils/getVariablesForRules';
import UpdateTagMutation from '../_graphql/mutations/documents/UpdateTagMutation';
import { callbackSave } from '../utils/messageMutation';
import CreateTagMutation from '../_graphql/mutations/documents/CreateTagMutation';
import { deleteTag } from '../pages/helpers/deleteTag';
import useRules from './useRules';
import useActionGuard from './useActionGuard';

const useTag = ({ tagId, tag, scope, offerId, globalVariablesFetch, isLoading }) => {
  const tagExist = isNotNil(tagId);
  const [state, setState] = useState(defaultTo({ color: DEFAULT_COLOR, itemType: 'document' }, tag));
  const [globalVariableCreated, setGlobalVariableCreated] = useState();
  const [isOpenCreateGlobalVariableModal, setIsOpenCreateGlobalVariableModal] = useState(null);
  const [globalVariables, setGlobalVariables] = useState([]);
  const { loading, run } = useActionGuard();

  useEffect(() => {
    if (not(isLoading) && isNotEmpty(globalVariablesFetch)) {
      setGlobalVariables(compose(
        defaultTo([]),
        getVariablesForRules
      )(globalVariablesFetch));
    }
  }, [globalVariablesFetch, isLoading]);

  useEffect(() => {
    if (isNotNil(globalVariableCreated)) {
      const { onUpdate, conditionIndex } = isOpenCreateGlobalVariableModal;
      onUpdate(assocPath(['conditions', conditionIndex, 'variable'], prop('name', globalVariableCreated)));
      setGlobalVariables([...globalVariables, { type: prop('type', globalVariableCreated), key: prop('name', globalVariableCreated) }]);
      setGlobalVariableCreated();
      setIsOpenCreateGlobalVariableModal(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalVariableCreated]);

  const history = useHistory();

  const isEdit = !isNil(tagId);
  const isValid = isNotNilOrEmpty(prop('name', state)) && isNotNilOrEmpty(prop('color', state)) && isNotNilOrEmpty(prop('itemType', state));

  const onItemUp = index => {
    setState(
      over(
        lensProp('itemsIds'),
        move(index, index - 1)
      )
    );
  };

  const onItemDown = index => {
    setState(
      over(
        lensProp('itemsIds'),
        move(index, index + 1)
      )
    );
  };

  const onSave = () => {
    if (isEdit) {
      const tag = pick(['name', 'color', 'itemsIds', 'rules'], state);
      run(`update-tag-${tagId}`, (done) => UpdateTagMutation({ tagId, tag }, done), callbackSave('tags', 'Le tag a bien été mis à jour.', history, scope, state, offerId));
    } else {
      const tag = compose(
        assoc('offerId', offerId),
        assoc('scope', scope)
      )(state);
      run(`create-tag-${offerId}`, (done) => CreateTagMutation({ tag }, done), callbackSave('tags', 'Le tag a bien été créé.', history, scope, state, offerId));
    }
  };

  const addTagRule = (itemId) => {
    let rules = defaultTo([])(prop('rules', state));
    const index = findIndex(propEq(itemId, 'itemId'))(rules);
    if (index > -1) {
      const rulesAtIndex = path([index, 'rules'], rules);
      rules = assocPath([index, 'rules'], [...rulesAtIndex, {}], rules);
    } else {
      rules = [...rules, { itemId, rules: [{}] }];
    }
    setState({ ...state, rules });
  };

  const removeTagRule = (itemId) => (ruleIndex) => () => {
    const rulesItemId = findIndex(propEq(itemId, 'itemId'))(prop('rules', state));
    ifElse(
      () => gt(length(path(['rules', rulesItemId, 'rules'], state)), 1),
      () => setState(over(
        lensPath(['rules', rulesItemId, 'rules']),
        remove(ruleIndex, 1)
      )),
      () => setState(over(
        lensPath(['rules']),
        remove(rulesItemId, 1)
      ))
    )(state);
  };

  const updateTagRule = (itemId) => (ruleIndex) => (fn) => {
    const rulesItemId = findIndex(propEq(itemId, 'itemId'))(prop('rules', state));
    setState(over(
      lensPath(['rules', rulesItemId, 'rules']),
      adjust(ruleIndex, fn)
    ));
  };

  const removeTag = () => {
    run(`delete-tag-${tagId}`, (done) => deleteTag(tagId, done), (ok, error) => {
      if (ok && !error) {
        history.push(`/offre/${offerId}/edition`);
      }
    });
  };

  const {
    getRulesAreValid,
    allRulesAreValid,
    getConditionsAreValid
  } = useRules(state);

  return {
    ...state,
    setState,
    isEdit,
    onSave,
    loading,
    isValid,
    onItemUp,
    onItemDown,
    getRulesAreValid,
    allRulesAreValid,
    getConditionsAreValid,
    addTagRule,
    removeTagRule,
    updateTagRule,
    tagExist,
    isOpenCreateGlobalVariableModal,
    setIsOpenCreateGlobalVariableModal,
    setGlobalVariableCreated,
    globalVariables,
    removeTag
  };
};

export default useTag;
